import { useState, useRef, useEffect, useCallback } from 'react';
import Icon from './Icon';

/**
 * The purchasing-total explanation — the "סה"כ רכש (כולל מע"מ)" card, shared by
 * BOTH payment tabs.
 *
 * One copy on purpose. The card shows the same number on /suppliers and
 * /suppliers/paid, so two hand-kept descriptions of it would be free to drift
 * into describing two different metrics — which is the exact bug the shared
 * PURCHASE_AMOUNT_CASE on the server exists to prevent.
 *
 * Wording is deliberately plain: no מע"מ mechanics, no doc-type names, no
 * mention of settled-vs-open credits. The reader wants to know what the number
 * means, not how the SQL reaches it.
 *
 * Two things this text is careful about:
 *
 * 1. "כל המסמכים", not "כל החשבוניות". The figure spans SUPPLIER_DOC_TYPES —
 *    delivery notes, consolidated invoices, invoice/receipts and transaction
 *    accounts as well as plain invoices. Saying "invoices" understated it by
 *    ₪37,428 of delivery notes on one production month, which is exactly the
 *    money the caveat below is about.
 *
 * 2. The closing caveat names DELIVERY NOTES, not 'לא תקין' documents. A
 *    delivery note's prices are often backfilled from the catalog, so the amount
 *    really does move when the invoice lands — the page already flags those
 *    suppliers with an asterisk under the table. 'לא תקין' was considered and
 *    rejected: it does not mean "pending", nothing is queued to resolve it, and
 *    on production data not one such document had an open credit behind it. A
 *    caveat pointing there would promise a correction that never arrives.
 */
export const PURCHASE_INFO = (
  <>
    כמה קנית מהספקים בתקופה שנבחרה, כולל מע״מ.
    <ul className="stat-info-list">
      <li>מחברים את כל המסמכים מהספקים</li>
      <li>מורידים את כל הזיכויים</li>
      <li>לא משנה אם כבר שילמת או עדיין לא</li>
    </ul>
    <p className="stat-info-note">
      סכום שמבוסס על תעודת משלוח בלבד עשוי להשתנות כשתגיע החשבונית.
    </p>
  </>
);

/**
 * A small "how is this calculated" note attached to a metric card's label.
 *
 * Deliberately a TAP target rather than a `title` tooltip: the payment screens
 * are used on a phone, and a native tooltip needs a hover that touch devices do
 * not have — the explanation would simply be unreachable for most of its
 * audience. Same reason it is a real <button>: it has to be focusable and
 * keyboard-operable, which a hover-only affordance never is.
 *
 * Closes on outside tap via `.menu-backdrop`, the same dismissal primitive the
 * page menus use, so a card note and an open ⋮ menu behave identically.
 */
const PANEL_MAX = 260;   // px — comfortable line length for two short sentences
const EDGE = 12;         // px — minimum gap to the viewport edge
const GAP = 7;           // px — distance from the trigger

// `ariaLabel` overrides the trigger's spoken name for a note that is not a
// "how is this calculated" explanation (e.g. the list behind a count).
export default function StatInfo({ label, ariaLabel, children, maxWidth = PANEL_MAX }) {
  const [pos, setPos] = useState(null);   // null = closed
  const btnRef = useRef(null);

  // Positioned from the trigger's rect in FIXED coordinates rather than by CSS
  // centering on the trigger. These cards sit in a 2-column grid, so a panel
  // centred on a card in the start column runs off the edge of a phone screen —
  // it did, and the first line of the explanation was clipped. Only measuring
  // against the viewport can clamp it, and CSS has no way to express that.
  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(maxWidth, window.innerWidth - EDGE * 2);
    const centred = r.left + r.width / 2 - width / 2;
    const left = Math.max(EDGE, Math.min(centred, window.innerWidth - width - EDGE));
    setPos({ top: r.bottom + GAP, left, width });
  }, [maxWidth]);

  // A fixed panel is placed once, so it has to be re-measured as the page moves.
  // It must RE-PLACE rather than close on scroll: focusing the button can itself
  // nudge the page, and a close-on-scroll handler ate the panel on the very tap
  // that opened it — the note never appeared on a phone. Only a trigger that has
  // actually left the viewport dismisses it, since a panel pointing at nothing
  // is worse than no panel.
  useEffect(() => {
    if (!pos) return undefined;
    const onMove = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) { setPos(null); return; }
      place();
    };
    window.addEventListener('scroll', onMove, true);   // capture: catches scrolls inside any container
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
    // `pos` is intentionally a presence check only — re-running on every
    // reposition would tear down and rebuild the listeners on each scroll frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!pos, place]);

  return (
    <span className="stat-info">
      <button
        ref={btnRef}
        type="button"
        className="stat-info-trigger"
        aria-label={ariaLabel || `איך מחושב: ${label}`}
        aria-expanded={!!pos}
        // The card itself is not clickable today, but stopPropagation keeps this
        // true if it ever becomes a drill-down: opening the note must never also
        // navigate away from the number being explained.
        onClick={(e) => { e.stopPropagation(); pos ? setPos(null) : place(); }}
      >
        <Icon name="info" size={13} />
      </button>

      {pos && (
        <>
          {/* Both the backdrop and the panel stop propagation, same reason as
              the trigger above: neither is a portal, so a click here is still
              a DOM descendant of whatever renders <StatInfo> and would
              otherwise bubble into that ancestor's own onClick — e.g. a tile
              that navigates on click would fire that navigation on every
              click anywhere on the page while this note is open (the
              backdrop covers the full viewport), or on any tap inside the
              note's own text. */}
          <div className="menu-backdrop" onClick={(e) => { e.stopPropagation(); setPos(null); }} />
          <div
            className="stat-info-panel"
            role="note"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </>
      )}
    </span>
  );
}
